import { useEffect, useMemo, useState } from 'react'
import { Alert, Button, Form, Input, Spin } from 'antd'
import { normalizePhone } from '../lib/phone'
import { useSupportPrefill, useSubmitSupportFormMutation } from '../hooks/useSupport'
import { useAuthSession } from '../lib/session'
import { useToast } from '../contexts/ToastContext'

type SupportFormInput = {
  name: string
  phone: string
  email: string
  subject: string
  message: string
}

export default function Support() {
  const session = useAuthSession()
  const userId = session?.currentUserId || ''
  const [form] = Form.useForm<SupportFormInput>()
  const { addToast } = useToast()
  const [showMessage, setShowMessage] = useState(false)

  const prefillQuery = useSupportPrefill(userId)
  const submitMutation = useSubmitSupportFormMutation()

  const fallbackValues = useMemo(
    () => ({
      name: session?.currentUsername || '',
      email: session?.email || '',
      phone: '',
    }),
    [session?.currentUsername, session?.email]
  )

  useEffect(() => {
    if (!prefillQuery.data) {
      form.setFieldsValue({
        name: fallbackValues.name,
        email: fallbackValues.email,
      })
      return
    }

    form.setFieldsValue({
      name: prefillQuery.data.name || fallbackValues.name,
      phone: prefillQuery.data.phone || fallbackValues.phone,
      email: prefillQuery.data.email || fallbackValues.email,
    })
  }, [form, prefillQuery.data, fallbackValues])

  return (
    <div className="rounded-2xl border border-brand-100 shadow-card bg-gradient-to-br from-white to-sky-50/35 p-6">
      <div className="mb-4">
        <h1 className="font-display text-2xl font-bold text-gray-900">Support</h1>
        <p className="text-sm text-gray-500 mt-1">
          Fill out the form below and your admin representative will contact you.
        </p>
      </div>

      {prefillQuery.isLoading && (
        <div className="min-h-24 flex items-center justify-center">
          <Spin />
        </div>
      )}

      {prefillQuery.isError && (
        <Alert
          className="mb-4"
          type="warning"
          showIcon
          message={(prefillQuery.error as Error)?.message || 'Unable to load profile details. You can still submit the form manually.'}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          name: fallbackValues.name,
          phone: '',
          email: fallbackValues.email,
          subject: '',
          message: '',
        }}
        onValuesChange={() => {
          if (showMessage) setShowMessage(false)
        }}
        onFinish={async (values) => {
          try {
            await submitMutation.mutateAsync({
              name: values.name.trim(),
              phone: values.phone.trim(),
              email: values.email.trim(),
              subject: values.subject.trim(),
              message: values.message.trim(),
            })
            form.resetFields()
            setShowMessage(true)
          } catch (error) {
            addToast(error instanceof Error ? error.message : 'Failed to submit support request.', 'error')
          }
        }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: 'Name is required' }]}
          >
            <Input placeholder="Name*" />
          </Form.Item>
          <Form.Item
            label="Contact Number"
            name="phone"
            rules={[
              { required: true, message: 'Contact number is required' },
              {
                validator: async (_, value: string) => {
                  if (!value) return
                  if (normalizePhone(value).length !== 10) {
                    throw new Error('Contact number must contain 10 digits')
                  }
                },
              },
            ]}
          >
            <Input placeholder="Contact Number*" />
          </Form.Item>
        </div>

        <Form.Item
          label="Email Address"
          name="email"
          rules={[
            { required: true, message: 'Email address is required' },
            { type: 'email', message: 'Enter a valid email address' },
          ]}
        >
          <Input placeholder="Email Address*" />
        </Form.Item>

        <Form.Item
          label="Subject"
          name="subject"
          rules={[{ required: true, message: 'Subject is required' }]}
        >
          <Input placeholder="Subject*" />
        </Form.Item>

        <Form.Item
          label="Message"
          name="message"
          rules={[{ required: true, message: 'Message is required' }]}
        >
          <Input.TextArea rows={5} placeholder="Message*" />
        </Form.Item>

        {showMessage && (
          <Alert
            className="mb-4"
            type="success"
            showIcon
            message="Thank you for submitting your support request. Your admin representative will contact you shortly."
          />
        )}

        <Form.Item noStyle shouldUpdate>
          {() => {
            const requiredFilled = (['name', 'phone', 'email', 'subject', 'message'] as const).every((field) =>
              String(form.getFieldValue(field) ?? '').trim()
            )
            const hasErrors = form
              .getFieldsError(['name', 'phone', 'email', 'subject', 'message'])
              .some((item) => item.errors.length > 0)

            return (
              <Button
                type="primary"
                htmlType="submit"
                loading={submitMutation.isPending}
                disabled={!requiredFilled || hasErrors}
              >
                Submit
              </Button>
            )
          }}
        </Form.Item>
      </Form>
    </div>
  )
}
